import { AvatarSelectOptionsInput } from './FieldCircleCheckboxV2'
import { useFormInput } from '../hooks/useFormInput'
import { FieldText } from './FieldText'
import { FieldOption } from './FieldOption'
import { PageAnchorTags, PageHeader, AccountSectionWrapper, ANCHOR_TAGS } from './AccountSectionWrapper'
import { AccountMeasurements } from './AccountMeasurements'
import { AccountShoeWardrobe } from './AccountShoeWardrobe'
import { useCallback, useMemo, useState } from 'preact/hooks'
import { AccountKidsProfileBrand } from './AccountKidsProfileBrand'
import { AccountKidsProfileSports } from './AccountKidsProfileSports'
import { useAccountLayoutContext } from './AccountProfileContext'
import { FieldFetchOnClick } from './FieldFetchOnClick'
import { getShoeTag } from '../helpers/getShoeTag'
import { getProfileIconIndex } from '../helpers/getProfileIcon'
import { genderOptions, profileIcons } from '../helpers/GlobalAssets'
import { FieldDate } from './FieldDate'
import { useRefetch } from '../hooks/useRefetch'
import { Form, Page } from './AccountPage'
import { useProfile } from '../hooks/useProfile'
import { useToggle } from '../hooks/useToggle'
import { birthdayStringToOmneoObject } from '../helpers/parseBirthday'
import { AccountKidsProfileWishlist } from './AccountKidsProfileWishlist'
import { ReceiveRewardBanner } from './ReceiveRewardBanner'
import { accountTranslations } from '../helpers/translations'
/**
 *  @typedef {import("../types/omneo").OmneoConnection} OmneoConnection
 *  @typedef {import("../types/omneo").ChildProfile} ChildProfile
 */

export const PROFILE_ICONS = profileIcons ?? [
  {
    icon: 'https://static-00.iconduck.com/assets.00/settings-icon-1024x1022-x2c1qvd9.png',
    background_colour: '#fdc145'
  },
  {
    icon: 'https://static-00.iconduck.com/assets.00/settings-icon-1024x1022-x2c1qvd9.png',
    background_colour: '#fdc145'
  },
  {
    icon: 'https://static-00.iconduck.com/assets.00/settings-icon-1024x1022-x2c1qvd9.png',
    background_colour: '#fdc145'
  }
]

export const AccountKidProfileAvatarSelector = ({ formInputs, handleOnChange, newProfile, errors }) => {
  return (
    <AccountSectionWrapper
      title={accountTranslations.kids_profiles.identifiers.title}
      anchorTag={ANCHOR_TAGS.IDENTIFIERS.tag}
    >
      <AvatarSelectOptionsInput
        className={'flex flex-row gap-2 pb-4'}
        iconSize={'small'}
        name={formInputs.avatar.key}
        value={formInputs.avatar.value ?? 0}
        required={true}
        label={'Avatar'}
        onChange={handleOnChange}
        options={PROFILE_ICONS.map((icon, i) => ({
          avatarUrl: icon.icon,
          value: i,
          backgroundColour: icon.background_colour
        }))}
      />

      <div className="flex flex-col gap-2">
        <FieldFetchOnClick name={formInputs.shoeTag.key} value={formInputs.shoeTag.value} onChange={handleOnChange} />
        {errors.shoeTag === 'duplicate' && (
          <div className={'text-xs text-system-error'}>
            {accountTranslations.form_labels.shoe_tag_exists}
          </div>
        )}
        <div className="px-4 py-2 rounded-lg bg-warning-bg">
          <p className="text-xs text-left text-primary-black md:text-center">
            {accountTranslations.onboarding.step_1.shoe_tag_description}
          </p>
        </div>
      </div>
    </AccountSectionWrapper>
  )
}

export const AccountKidProfileProfileDetailsSelector = ({ formInputs, handleOnChange, newProfile }) => {
  return (
    <AccountSectionWrapper
      title={accountTranslations.kids_profiles.new_profile.details}
      anchorTag={ANCHOR_TAGS.PROFILE_DETAILS.tag}
    >
      <div className={'flex flex-row gap-4'}>
        <FieldText
          name={formInputs.firstName.key}
          value={formInputs.firstName.value}
          required={true}
          label={accountTranslations.form_labels.first_name}
          placeholder={accountTranslations.form_placeholders.first_name}
          autoComplete="given-name"
          onChange={handleOnChange}
        />
        <FieldText
          name={formInputs.lastName.key}
          value={formInputs.lastName.value}
          label={accountTranslations.form_labels.last_name}
          placeholder={accountTranslations.form_placeholders.last_name}
          autoComplete="family-name"
          onChange={handleOnChange}
        />
      </div>
      <div className={'flex flex-col gap-4 md:flex-row'}>
        <FieldDate
          onChange={handleOnChange}
          name={formInputs.birthday.key}
          value={formInputs.birthday.value}
          label={accountTranslations.form_labels.birthday}
          showDay={false}
        />
        <FieldOption
          {...{
            label: accountTranslations.form_labels.gender,
            name: formInputs.gender.key,
            value: formInputs.gender.value,
            onChange: handleOnChange,
            options: genderOptions,
            customType: 'input'
          }}
        />
      </div>
      {!formInputs.birthday.value && (
        <div>
          <ReceiveRewardBanner>{accountTranslations.kids_profiles.new_profile.birthday_reward}</ReceiveRewardBanner>
        </div>
      )}
    </AccountSectionWrapper>
  )
}

/**
 *
 * @param {{profile: ChildProfile}} param0
 * @returns
 */
const KidProfile = ({ profile, handleRefetch, connectionId, isNewProfile }) => {
  const { getChildren } = useProfile()
  const { isActive, handleToggle } = useToggle()
  const { omneoClient, cacheConfig } = useAccountLayoutContext()
  const [errors, setErrors] = useState({})

  const { formInputs, handleOnChange, hasChanged } = useFormInput({
    firstName: profile?.first_name ?? '',
    lastName: profile?.last_name ?? '',
    birthday: profile?.birth_date ?? '',
    gender: profile?.gender ?? undefined,
    shoeTag: getShoeTag(profile),
    avatar: getProfileIconIndex(profile, PROFILE_ICONS)
  })

  const resetErrors = () => {
    setErrors({})
  }

  return (
    <>
      <div className={'flex flex-col gap-4'}>
        <PageHeader title={`${profile.first_name}'s profile`} />
        <PageAnchorTags />
      </div>
      <Form
        isActive={isActive}
        onSubmit={async (e) => {
          e.preventDefault()

          resetErrors()
          handleToggle()
          const index = parseInt(formInputs.avatar.value)
          const url = PROFILE_ICONS[index]?.icon ?? ''

          const data = {
            first_name: formInputs.firstName.value,
            last_name: formInputs.lastName.value,
            ...(formInputs.birthday.value ? birthdayStringToOmneoObject(formInputs.birthday.value) : {}),
            gender: formInputs.gender.value,
            avatar_url: url ? 'https:'.concat(url) : undefined
          }
          await omneoClient.updateProfile(data)

          const shoeTagIdentifier = profile.identities.find((identity) => identity.handle === 'shoetag')
          if (shoeTagIdentifier && formInputs.shoeTag.value !== shoeTagIdentifier.identifier) {
            const res = await omneoClient.updateProfileIdentity(shoeTagIdentifier.id, {
              identifier: formInputs.shoeTag.value
            })

            if (!res) {
              setErrors({ shoeTag: 'duplicate' })
              handleToggle()
              document.querySelector(`#${ANCHOR_TAGS.IDENTIFIERS.tag}`)?.scrollIntoView({ behavior: 'smooth' })
              return
            }
          }

          handleToggle()
          cacheConfig.updateCacheConfig(`child_${profile.id}`, null)
          await getChildren(true)

          handleRefetch()
        }}
        hasChanged={hasChanged}
      >
        <AccountKidProfileAvatarSelector
          profile={profile}
          formInputs={formInputs}
          handleOnChange={handleOnChange}
          errors={errors}
        />
        <AccountKidProfileProfileDetailsSelector
          profile={profile}
          formInputs={formInputs}
          handleOnChange={handleOnChange}
        />
      </Form>
      <>
        <AccountShoeWardrobe profile={profile} handleRefetch={handleRefetch} />
        <AccountMeasurements
          props={{
            connectionId
          }}
        />
        <AccountKidsProfileBrand profile={profile} connectionId={connectionId} handleRefetch={handleRefetch} />
        <AccountKidsProfileSports profile={profile} connectionId={connectionId} handleRefetch={handleRefetch} />
        <AccountKidsProfileWishlist profile={profile} connectionId={connectionId} handleRefetch={handleRefetch} />
      </>
    </>
  )
}

/**
 * Unsure if we will have all the children information when fetch all the
 * childrens data
 * @param {{profile: ChildProfile, connectionId: number, isNewProfile: boolean}} param0
 */
export const AccountKidProfile = ({ connectionId, isNewProfile }) => {
  const { omneoClient, cacheConfig } = useAccountLayoutContext()
  const [childProfile, setChildProfile] = useState()
  const { refetch, handleRefetch } = useRefetch()

  const handleFetchChildData = useCallback(async () => {
    // Get assigned items
    const child = await omneoClient.getChildProfileByID(connectionId)
    const assignedItems = await omneoClient.getOrCreateList(undefined)

    // Get wishlist items
    const wishlist = await omneoClient.getOrCreateList(undefined, 'wishlist')

    // Get all children
    const children = await omneoClient.profileClient.getChildren()

    const unassignedItems = await omneoClient.profileClient.getUnassignedTransactionItems({ filterOutExpired: false, filterReturned: true })
    const unexpiredUnassignedItems = await omneoClient.profileClient.getUnassignedTransactionItems({ filterOutExpired: true, filterReturned: true })
    setChildProfile(() => ({ ...child, assignedItems, wishlist, unassignedItems, unexpiredUnassignedItems, children }))
    const balance = await omneoClient.profileClient.getBalance()
    cacheConfig.updateCacheConfig('Balance', { ...balance })
  }, [])

  return (
    <Page
      props={{
        page: {
          title: ''
        },
        suspense: {
          refetch,
          asyncCallback: handleFetchChildData,
          loadingState: (
            <div>
              <div className="inline-block mb-4 w-fit animate-pulse bg-primary-grey">
                <h1 className="invisible inline font-bold h1 text-primary-grey">{accountTranslations.kids_profiles.kids_profile_title}</h1>
              </div>
              <div className="flex flex-row gap-6 mb-8 md:mb-10">
                <div className="inline-block w-fit animate-pulse bg-primary-grey">
                  <a className="invisible underline btn btn-text btn-md btn-inline text-primary-grey underline-offset-2">
                    {accountTranslations.kids_profiles.identifiers}
                  </a>
                </div>
                <div className="inline-block w-fit animate-pulse bg-primary-grey">
                  <a className="invisible underline btn btn-text btn-md btn-inline text-primary-grey underline-offset-2">
                    {accountTranslations.kids_profiles.new_profile.details}
                  </a>
                </div>
                <div className="inline-block w-fit animate-pulse bg-primary-grey">
                  <a className="invisible underline btn btn-text btn-md btn-inline text-primary-grey underline-offset-2">
                    {accountTranslations.kids_profiles.shoe_wardrobe.title}
                  </a>
                </div>
                <div className="inline-block w-fit animate-pulse bg-primary-grey">
                  <a className="invisible underline btn btn-text btn-md btn-inline text-primary-grey underline-offset-2">
                    {accountTranslations.kids_profiles.measurements.title}
                  </a>
                </div>
                <div className="inline-block w-fit animate-pulse bg-primary-grey">
                  <a className="invisible underline btn btn-text btn-md btn-inline text-primary-grey underline-offset-2">
                    {accountTranslations.kids_profiles.favourite_brands.title}
                  </a>
                </div>
                <div className="inline-block w-fit animate-pulse bg-primary-grey">
                  <a className="invisible underline btn btn-text btn-md btn-inline text-primary-grey underline-offset-2">
                    {accountTranslations.kids_profiles.favourite_sports.title}
                  </a>
                </div>
                <div className="inline-block w-fit animate-pulse bg-primary-grey">
                  <a className="invisible underline btn btn-text btn-md btn-inline text-primary-grey underline-offset-2">
                    {accountTranslations.kids_profiles.wishlist.title}
                  </a>
                </div>
              </div>
              <div className="inline-block mb-6 w-fit animate-pulse bg-primary-grey">
                <div className="font-bold h3 text-primary-grey">{accountTranslations.kids_profiles.identifiers.title}</div>
              </div>
              <div className="flex gap-2 pb-4 mb-6">
                <div className="w-24 rounded-full aspect-1 max-w-24 animate-pulse bg-primary-grey" />
                <div className="w-24 rounded-full aspect-1 max-w-24 animate-pulse bg-primary-grey" />
                <div className="w-24 rounded-full aspect-1 max-w-24 animate-pulse bg-primary-grey" />
                <div className="w-24 rounded-full aspect-1 max-w-24 animate-pulse bg-primary-grey" />
              </div>
              <div className="mb-4 h-[144px] animate-pulse bg-primary-grey md:h-[126px]" />
              <div className="inline-block mb-6 w-fit animate-pulse bg-primary-grey">
                <div className="invisible font-bold h3 text-primary-grey">{accountTranslations.kids_profiles.profile_details.title}</div>
              </div>
            </div>
          )
        }
      }}
    >
      <KidProfile
        handleRefetch={handleRefetch}
        profile={childProfile}
        connectionId={connectionId}
        isNewProfile={isNewProfile}
      />
    </Page>
  )
}
